/**
 * Cost Analytics Component
 * Per-agent cost breakdown with optimization suggestions
 * Shows historical costs, provider comparison, and projections
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import {
  DollarSign,
  TrendingDown,
  TrendingUp,
  Lightbulb,
  Download,
  Loader2,
} from 'lucide-react';
import type { AgentInfo } from '@/lib/agentClient';
import { agentClient } from '@/lib/agentClient';
import { formatCost, getCostTierColor, compareCosts } from '../utils/cost-calculator';
import { getProviderName, PROVIDER_COLORS } from '../utils/provider-config';

interface CostAnalyticsProps {
  agentId: string;
}

interface CostData {
  totalCost: number;
  totalExecutions: number;
  avgCostPerExecution: number;
  trend30d: Array<{ date: string; cost: number; executions: number }>;
  providerBreakdown: Array<{ provider: string; cost: number; percentage: number }>;
  stepBreakdown: Array<{ step: string; cost: number; executions: number }>;
}

interface OptimizationSuggestion {
  type: 'model_downgrade' | 'step_merge' | 'timeout_reduction' | 'provider_switch';
  title: string;
  description: string;
  currentState: string;
  suggestedState: string;
  estimatedSavings: number;
  savingsPercentage: number;
  impact: 'none' | 'low' | 'medium' | 'high';
}

export default function CostAnalytics({ agentId }: CostAnalyticsProps) {
  const [loading, setLoading] = useState(true);
  const [agent, setAgent] = useState<AgentInfo | null>(null);
  const [costData, setCostData] = useState<CostData | null>(null);
  const [suggestions, setSuggestions] = useState<OptimizationSuggestion[]>([]);

  useEffect(() => {
    loadCostData();
  }, [agentId]);

  const loadCostData = async () => {
    try {
      setLoading(true);

      // Load agent details
      const agents = await agentClient.discoverAgents();
      const foundAgent = agents.find((a) => a.id === agentId);
      if (!foundAgent) throw new Error('Agent not found');
      setAgent(foundAgent);

      // Generate mock cost data (in production, this would come from analytics API)
      const mockCostData = generateMockCostData();
      setCostData(mockCostData);

      // Generate optimization suggestions
      if (foundAgent.volcano_workflow) {
        const mockSuggestions = generateOptimizationSuggestions(foundAgent);
        setSuggestions(mockSuggestions);
      }
    } catch (err: any) {
      console.error('Failed to load cost data:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateMockCostData = (): CostData => {
    // Generate 30 days of data
    const trend30d = [];
    let baseExecutions = 50;
    for (let i = 30; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const executions = Math.floor(baseExecutions + Math.random() * 20 - 10);
      const costPerExec = 0.005 + Math.random() * 0.003;
      trend30d.push({
        date: date.toISOString().split('T')[0],
        cost: executions * costPerExec,
        executions,
      });
    }

    const totalCost = trend30d.reduce((sum, d) => sum + d.cost, 0);
    const totalExecutions = trend30d.reduce((sum, d) => sum + d.executions, 0);

    return {
      totalCost,
      totalExecutions,
      avgCostPerExecution: totalCost / totalExecutions,
      trend30d,
      providerBreakdown: [
        { provider: 'Anthropic Claude', cost: totalCost * 0.6, percentage: 60 },
        { provider: 'OpenAI GPT', cost: totalCost * 0.4, percentage: 40 },
      ],
      stepBreakdown: [
        { step: 'Step 1: Extract', cost: totalCost * 0.2, executions: totalExecutions },
        { step: 'Step 2: Analyze', cost: totalCost * 0.5, executions: totalExecutions },
        { step: 'Step 3: Format', cost: totalCost * 0.3, executions: totalExecutions },
      ],
    };
  };

  const generateOptimizationSuggestions = (agent: AgentInfo): OptimizationSuggestion[] => {
    const steps = agent.volcano_workflow?.steps;
    if (!Array.isArray(steps) || steps.length === 0) return [];

    const suggestions: OptimizationSuggestion[] = [];

    // Check for expensive models
    steps.forEach((step, index) => {
      if (step.model?.includes('opus') || step.model?.includes('gpt-4')) {
        suggestions.push({
          type: 'model_downgrade',
          title: `Downgrade Model in Step ${index + 1}`,
          description: 'Switch to a more cost-effective model for this step',
          currentState: step.model,
          suggestedState: step.provider === 'anthropic' ? 'claude-3-5-sonnet' : 'gpt-4o',
          estimatedSavings: 0.05,
          savingsPercentage: 75,
          impact: 'low',
        });
      }
    });

    // Check for high timeouts
    steps.forEach((step, index) => {
      if (step.timeout > 60000) {
        suggestions.push({
          type: 'timeout_reduction',
          title: `Reduce Timeout in Step ${index + 1}`,
          description: 'Lower timeout to prevent unnecessary waiting on failures',
          currentState: `${step.timeout / 1000}s`,
          suggestedState: '30s',
          estimatedSavings: 0.01,
          savingsPercentage: 10,
          impact: 'none',
        });
      }
    });

    // Suggest provider switching
    if (steps.length > 1) {
      const comparison = compareCosts('anthropic', 'claude-3-5-sonnet', 'openai', 'gpt-4o');
      if (comparison.savings_percentage > 20) {
        suggestions.push({
          type: 'provider_switch',
          title: 'Switch Provider for Cost Savings',
          description: 'OpenAI GPT-4o is more cost-effective for some steps',
          currentState: 'Mixed providers',
          suggestedState: 'Optimize per step',
          estimatedSavings: 0.08,
          savingsPercentage: comparison.savings_percentage,
          impact: 'low',
        });
      }
    }

    return suggestions;
  };

  const exportReport = () => {
    if (!costData || !agent) return;

    const csvContent = [
      ['Date', 'Executions', 'Cost'],
      ...costData.trend30d.map((d) => [d.date, d.executions, d.cost.toFixed(4)]),
    ]
      .map((row) => row.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cost-analytics-${agentId}-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'none': return 'bg-green-100 text-green-800';
      case 'low': return 'bg-blue-100 text-blue-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'high': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!costData || !agent) {
    return (
      <Alert>
        <AlertDescription>No cost data available for this agent</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Cost Analytics</h2>
          <p className="text-sm text-gray-600">{agent.name}</p>
        </div>
        <Button variant="outline" onClick={exportReport}>
          <Download className="w-4 h-4 mr-2" />
          Export Report
        </Button>
      </div>

      {/* Cost Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Cost (30d)</p>
                <p className="text-3xl font-bold text-gray-900">
                  {formatCost(costData.totalCost)}
                </p>
              </div>
              <DollarSign className="w-10 h-10 text-gray-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Executions</p>
                <p className="text-3xl font-bold text-gray-900">
                  {costData.totalExecutions.toLocaleString()}
                </p>
              </div>
              <TrendingUp className="w-10 h-10 text-gray-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Avg Cost/Execution</p>
                <p className="text-3xl font-bold text-gray-900">
                  {formatCost(costData.avgCostPerExecution)}
                </p>
              </div>
              <div
                className={`px-3 py-1 rounded-full text-sm font-semibold ${getCostTierColor(
                  costData.avgCostPerExecution
                )}`}
              >
                {costData.avgCostPerExecution < 0.01
                  ? 'Low'
                  : costData.avgCostPerExecution < 0.1
                  ? 'Medium'
                  : 'High'}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cost Trend Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Cost Trend (Last 30 Days)</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={costData.trend30d}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="cost" stroke="#2563eb" name="Cost ($)" />
              <Line type="monotone" dataKey="executions" stroke="#16a34a" name="Executions" />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Provider Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Provider Cost Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={costData.providerBreakdown}
                  dataKey="cost"
                  nameKey="provider"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={(entry) => `${entry.percentage}%`}
                >
                  {costData.providerBreakdown.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={
                        entry.provider.includes('Anthropic')
                          ? PROVIDER_COLORS.anthropic.primary
                          : PROVIDER_COLORS.openai.primary
                      }
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-4 space-y-2">
              {costData.providerBreakdown.map((item, index) => (
                <div key={index} className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{item.provider}</span>
                  <span className="font-semibold">{formatCost(item.cost)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Step Cost Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {costData.stepBreakdown.map((step, index) => {
                const percentage = (step.cost / costData.totalCost) * 100;
                return (
                  <div key={index}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="font-medium text-gray-900">{step.step}</span>
                      <span className="font-semibold">{formatCost(step.cost)}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {percentage.toFixed(1)}% of total
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Optimization Suggestions */}
      {suggestions.length > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-orange-900">
              <Lightbulb className="w-5 h-5" />
              Optimization Suggestions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {suggestions.map((suggestion, index) => (
              <Card key={index} className="bg-white">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-1">
                        {suggestion.title}
                      </h4>
                      <p className="text-sm text-gray-600 mb-2">
                        {suggestion.description}
                      </p>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Current:</span>
                          <span className="ml-2 font-medium">{suggestion.currentState}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Suggested:</span>
                          <span className="ml-2 font-medium text-green-700">
                            {suggestion.suggestedState}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="ml-4 text-right">
                      <div className="text-2xl font-bold text-green-600">
                        <TrendingDown className="w-6 h-6 inline mr-1" />
                        {suggestion.savingsPercentage}%
                      </div>
                      <div className="text-xs text-gray-600">
                        ~{formatCost(suggestion.estimatedSavings)}/month
                      </div>
                      <Badge className={`mt-2 ${getImpactColor(suggestion.impact)}`}>
                        {suggestion.impact} impact
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

